import { useAppSelector } from "@/store/hooks";
import { Category, Lesson } from "@/store/slices/storeSlice";
import { useState } from "react";
import { CodesLessonsAndCatsI } from "./CodeManagement/EditCode";

const DynamicCategorySelector = ({
  styles = "block flex-1 border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-primary",
  btnStyles = "h-fit px-2 py-2 rounded-md bg-primary text-white hover:bg-primary/90 hover:cursor-pointer transition duration-300",
  catFieldName,
  lessonFieldName,
  alreadyChosenCats,
  filteredCats,
  filteredLessons,
  showConfirmSelectionBtn = false,
  confirmSelectionBtnText = "أضف هذا الإختيار",
  excludeCatsThatHasLessons = true,
  showLessonsSelector = false,
  isCreatingNewCategory = false,
  fieldRef,
  categoryUpdateHanlderFunction,
  lessonUpdateHanlderFunction,
  confirmSelectionHandlerFunction,
}: {
  styles?: string;
  btnStyles?: string;
  catFieldName?: string;
  lessonFieldName?: string;
  alreadyChosenCats?: number[];
  filteredCats?: Category[] | null;
  filteredLessons?:
    | (
        | Lesson
        | {
            ID: number;
            post_title: string;
            term_taxonomy_id: number;
          }
      )[]
    | null;
  confirmSelectionBtnText?: string;
  showConfirmSelectionBtn?: boolean;
  excludeCatsThatHasLessons?: boolean;
  showLessonsSelector?: boolean;
  isCreatingNewCategory?: boolean;
  fieldRef?: React.RefObject<HTMLSelectElement>;
  categoryUpdateHanlderFunction?: (
    value: string,
    chosenCatsIds: number[]
  ) => void;
  lessonUpdateHanlderFunction?: (value: any, chosenCatsIds?: number[]) => void;
  confirmSelectionHandlerFunction?: (
    value: any,
    itemType: "category" | "lesson",
    chosenCatsIds?: number[]
  ) => void;
}) => {
  const { portal } = useAppSelector((state) => state.store);
  const initialCats = filteredCats || portal?.cats;
  const allLessons = filteredLessons || portal?.data;
  const allCats = excludeCatsThatHasLessons
    ? initialCats?.filter(
        (cat) =>
          !allLessons.find(
            (lesson) => +lesson.term_taxonomy_id === +cat.term_id
          )
      )
    : initialCats;
  const [chosenCatsIds, setChosenCatsIds] = useState<number[]>(
    alreadyChosenCats ? [0, ...alreadyChosenCats] : [0]
  );
  const [chosenLessonId, setChosenLessonId] = useState<number>(0);
  const currentCatHasChildren = allCats?.find(
    (cat) =>
      +cat.parent === chosenCatsIds[chosenCatsIds.length - 1] &&
      +cat.parent !== 0
  );

  return (
    <div className="space-y-4">
      <span className="flex flex-wrap gap-2 items-center">
        <select
          ref={fieldRef}
          id="cat-0"
          className={styles}
          aria-label="القسم الرئيسي"
          name={catFieldName}
          required
          onChange={(e) =>
            setChosenCatsIds((prev) => [prev[0], +e.target.value])
          }
        >
          <option value={0} selected={chosenCatsIds?.length === 1}>
            {isCreatingNewCategory ? "قسم رئيسي جديد" : "اختر القسم"}
          </option>
          {allCats
            ?.filter((cat) => cat.parent === 0)
            .map((cat) => (
              <option
                value={cat.term_id}
                selected={chosenCatsIds[1] === cat.term_id}
              >
                {cat.name.split("|")[0]}
              </option>
            ))}
        </select>
        {!showLessonsSelector &&
          showConfirmSelectionBtn &&
          chosenCatsIds.length === 2 && (
            <button
              type="button"
              className={btnStyles}
              onClick={() => {
                const obj = allCats.find(
                  (cat) =>
                    Number(cat.term_id) ===
                    chosenCatsIds[chosenCatsIds.length - 1]
                );
                confirmSelectionHandlerFunction?.(
                  {
                    id: obj?.term_id.toString(),
                    title: obj?.name.split("|")[0],
                    slug: obj?.slug,
                    parent: obj?.parent,
                  } as CodesLessonsAndCatsI[keyof CodesLessonsAndCatsI],
                  "category",
                  chosenCatsIds
                );
                setChosenCatsIds([0]);
              }}
            >
              {confirmSelectionBtnText}
            </button>
          )}
      </span>
      {chosenCatsIds
        .filter((catId) => catId !== 0)
        .map((catId, idx) => {
          const hasChildren =
            allCats?.filter((cat) => cat.parent == catId).length > 0;
          if (hasChildren || isCreatingNewCategory)
            return (
              <span className="flex flex-wrap gap-2 items-center">
                <select
                  key={catId}
                  id={catId.toString()}
                  ref={fieldRef}
                  className={styles}
                  aria-label={"القسم الرئيسي رقم " + idx}
                  name={catFieldName}
                  required
                  onChange={(e) => {
                    setChosenCatsIds((prev) =>
                      Array.from(
                        new Set([...prev.slice(0, idx + 2), +e.target.value])
                      )
                    );
                    categoryUpdateHanlderFunction?.(
                      e.target.value,
                      chosenCatsIds
                    );
                    setChosenLessonId(0);
                  }}
                >
                  <option
                    key={idx + 1}
                    value={chosenCatsIds[idx + 1]}
                    selected={!chosenCatsIds[idx + 2]}
                  >
                    {isCreatingNewCategory
                      ? `قسم جديد متفرع من ( 
                  ${
                    allCats
                      ?.find(
                        (cat) =>
                          +cat.term_taxonomy_id ===
                          Number(chosenCatsIds[idx + 1])
                      )
                      ?.name.split("|")[0]
                  } 
                  )`
                      : "اختر القسم الفرعي"}
                  </option>
                  {allCats
                    ?.filter((cat) => cat.parent == catId)
                    .map((cat) => (
                      <option
                        key={cat.term_id}
                        value={cat.term_id}
                        selected={chosenCatsIds[idx + 2] === cat.term_id}
                      >
                        {cat.name.split("|")[0]}
                      </option>
                    ))}
                </select>
                {!showLessonsSelector &&
                  showConfirmSelectionBtn &&
                  catId === chosenCatsIds[chosenCatsIds.length - 2] && (
                    <button
                      type="button"
                      className={btnStyles}
                      onClick={() => {
                        const obj = allCats.find(
                          (cat) =>
                            Number(cat.term_id) ===
                            chosenCatsIds[chosenCatsIds.length - 1]
                        );
                        confirmSelectionHandlerFunction?.(
                          {
                            id: obj?.term_id.toString(),
                            title: obj?.name.split("|")[0],
                            slug: obj?.slug,
                            parent: obj?.parent,
                          } as CodesLessonsAndCatsI[keyof CodesLessonsAndCatsI],
                          "category",
                          chosenCatsIds
                        );
                        setChosenCatsIds([0]);
                      }}
                    >
                      {confirmSelectionBtnText}
                    </button>
                  )}
              </span>
            );
        })}
      {!currentCatHasChildren &&
        showLessonsSelector &&
        chosenCatsIds.length > 1 && (
          <span className="flex flex-wrap gap-2 items-center">
            <select
              ref={showLessonsSelector ? fieldRef : null}
              className={styles}
              aria-label="إختر الحصة"
              required
              name={lessonFieldName}
              onChange={(e) => {
                lessonUpdateHanlderFunction?.(e.target.value);
                setChosenLessonId(+e.target.value);
              }}
            >
              <option value="" selected>
                إختر الحصة
              </option>
              {allLessons
                .filter(
                  (lesson) =>
                    +lesson.term_taxonomy_id ===
                    chosenCatsIds[chosenCatsIds.length - 1]
                )
                .map((lesson) => (
                  <option key={lesson.ID} value={lesson.ID}>
                    {lesson.post_title}
                  </option>
                ))}
            </select>
            {showLessonsSelector &&
              showConfirmSelectionBtn &&
              chosenCatsIds.length > 1 &&
              !!chosenLessonId && (
                <button
                  type="button"
                  className={btnStyles}
                  onClick={() => {
                    const obj = allLessons.find(
                      (lesson) => Number(lesson.ID) === chosenLessonId
                    );
                    confirmSelectionHandlerFunction?.(
                      {
                        id: obj?.ID,
                        title: obj?.post_title?.split("|")[0],
                        slug: (obj as any)?.slug,
                        parent: obj?.term_taxonomy_id,
                      } as CodesLessonsAndCatsI[keyof CodesLessonsAndCatsI],
                      "lesson",
                      chosenCatsIds
                    );
                    setChosenLessonId(0);
                    setChosenCatsIds([0]);
                  }}
                >
                  {confirmSelectionBtnText}
                </button>
              )}
          </span>
        )}
    </div>
  );
};

export default DynamicCategorySelector;
